import React from 'react';
import { useSettingsStore } from '../../state/settingsStore';
import { motion } from 'framer-motion';
import { soundManager } from '../../utils/sounds';
import {
  SpeakerWaveIcon,
  MusicalNoteIcon,
  CursorArrowRaysIcon,
  PlayCircleIcon,
  BellIcon,
  EyeIcon,
  BoltIcon,
  SwatchIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  HandRaisedIcon,
  StarIcon,
} from '@heroicons/react/24/outline';

// Settings category component
const SettingsGroup: React.FC<{
  title: string;
  description: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, description, icon, children }) => (
  <div className="arcade-panel pixel-corners rounded-xl p-4 sm:p-6">
    <div className="mb-4 flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-arcade-cyan/10 text-arcade-cyan">
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-white">{title}</h3>
        <p className="text-xs text-white/50">{description}</p>
      </div>
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

// Enhanced toggle component
interface ToggleRowProps {
  label: string;
  description: string;
  value: boolean;
  onChange: () => void;
  icon?: React.ReactNode;
  onPreview?: () => void;
}

const ToggleRow: React.FC<ToggleRowProps> = ({
  label,
  description,
  value,
  onChange,
  icon,
  onPreview,
}) => {
  const handleToggle = () => {
    onChange();
    // Play sound on toggle for audio feedback
    if (onPreview) {
      onPreview();
    } else {
      soundManager.play('click');
    }
  };

  return (
    <div className="group flex items-center justify-between gap-4 rounded-lg border border-white/5 bg-white/[0.02] p-3 sm:p-4 transition hover:border-white/10 hover:bg-white/[0.04]">
      <div className="flex items-center gap-3">
        {icon && <div className="text-white/40">{icon}</div>}
        <div>
          <p className="font-medium text-white/90">{label}</p>
          <p className="text-xs text-white/40">{description}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={handleToggle}
        className="btn-press focus:outline-none focus:ring-2 focus:ring-arcade-cyan/50 focus:ring-offset-2 focus:ring-offset-black"
      >
        <div
          className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 ${
            value ? 'bg-arcade-cyan' : 'bg-white/10'
          }`}
        >
          <motion.span
            layout
            className={`inline-block h-5 w-5 rounded-full shadow-md transition-colors ${
              value ? 'bg-white' : 'bg-white/60'
            }`}
            animate={{ x: value ? 32 : 4 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          />
          {/* Active glow */}
          {value && (
            <motion.div
              className="absolute inset-0 rounded-full"
              initial={{ boxShadow: '0 0 0 0 rgba(0, 255, 255, 0)' }}
              animate={{ boxShadow: '0 0 12px 2px rgba(0, 255, 255, 0.3)' }}
              transition={{ duration: 0.2 }}
            />
          )}
        </div>
      </button>
    </div>
  );
};

export const SettingsPage: React.FC = () => {
  const {
    motionReduced,
    colorBlindMode,
    autoplayAnimations,
    soundEnabled,
    soundCategories,
    oneHandedMode,
    reducedAnimations,
    largeTouchTargets,
    toggleAutoplay,
    toggleColorBlind,
    toggleMotion,
    toggleSound,
    toggleSoundCategory,
    toggleOneHandedMode,
    toggleReducedAnimations,
    toggleLargeTouchTargets,
  } = useSettingsStore();

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mx-auto max-w-2xl space-y-8 py-8"
    >
      {/* Header */}
      <header>
        <h1 className="neon-text font-display text-3xl font-bold">Settings</h1>
        <p className="mt-2 text-sm text-white/50">Customize your gaming experience</p>
      </header>

      {/* Audio Settings */}
      <SettingsGroup
        title="Audio"
        description="Sound effects and music preferences"
        icon={<SpeakerWaveIcon className="h-5 w-5" />}
      >
        <ToggleRow
          label="Sound Effects"
          description="Master toggle for all sound effects"
          value={soundEnabled}
          onChange={toggleSound}
          onPreview={() => {
            if (!soundEnabled) {
              soundManager.play('powerup');
            }
          }}
          icon={<MusicalNoteIcon className="h-4 w-4" />}
        />

        {/* Sound Category Toggles - only show when sound is enabled */}
        {soundEnabled && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="ml-4 space-y-3 border-l-2 border-white/10 pl-4"
          >
            <ToggleRow
              label="UI Sounds"
              description="Button clicks, selections, and error feedback"
              value={soundCategories.ui}
              onChange={() => toggleSoundCategory('ui')}
              onPreview={() => {
                if (!soundCategories.ui) {
                  soundManager.play('click');
                }
              }}
              icon={<CursorArrowRaysIcon className="h-4 w-4" />}
            />

            <ToggleRow
              label="Gameplay Sounds"
              description="Combos, wins, losses, cards, and scoring effects"
              value={soundCategories.gameplay}
              onChange={() => toggleSoundCategory('gameplay')}
              onPreview={() => {
                if (!soundCategories.gameplay) {
                  soundManager.play('combo');
                }
              }}
              icon={<PlayCircleIcon className="h-4 w-4" />}
            />

            <ToggleRow
              label="Notifications"
              description="Match ready alerts, achievements, and level ups"
              value={soundCategories.notification}
              onChange={() => toggleSoundCategory('notification')}
              onPreview={() => {
                if (!soundCategories.notification) {
                  soundManager.play('achievement');
                }
              }}
              icon={<BellIcon className="h-4 w-4" />}
            />
          </motion.div>
        )}
      </SettingsGroup>

      {/* Accessibility Settings */}
      <SettingsGroup
        title="Accessibility"
        description="Make the game more comfortable for you"
        icon={<EyeIcon className="h-5 w-5" />}
      >
        <ToggleRow
          label="Reduce Motion"
          description="Minimize animations, screen shake, and particle effects"
          value={motionReduced}
          onChange={toggleMotion}
          icon={<BoltIcon className="h-4 w-4" />}
        />

        <ToggleRow
          label="Color Blind Mode"
          description="Use shapes and patterns instead of colors alone"
          value={colorBlindMode}
          onChange={toggleColorBlind}
          icon={<SwatchIcon className="h-4 w-4" />}
        />
      </SettingsGroup>

      {/* Gameplay Settings */}
      <SettingsGroup
        title="Gameplay"
        description="Tweak how the game plays"
        icon={<PlayCircleIcon className="h-5 w-5" />}
      >
        <ToggleRow
          label="Autoplay Animations"
          description="Automatically show scoring breakdowns and combo pop-ups"
          value={autoplayAnimations}
          onChange={toggleAutoplay}
          icon={<ArrowPathIcon className="h-4 w-4" />}
        />
      </SettingsGroup>

      {/* Mobile Settings */}
      <SettingsGroup
        title="Mobile"
        description="Optimize for mobile and touch devices"
        icon={<DevicePhoneMobileIcon className="h-5 w-5" />}
      >
        <ToggleRow
          label="One-Handed Mode"
          description="Position controls at the bottom of the screen"
          value={oneHandedMode}
          onChange={toggleOneHandedMode}
          icon={<HandRaisedIcon className="h-4 w-4" />}
        />

        <ToggleRow
          label="Reduced Animations"
          description="Faster transitions, less motion (saves battery)"
          value={reducedAnimations}
          onChange={toggleReducedAnimations}
          icon={<BoltIcon className="h-4 w-4" />}
        />

        <ToggleRow
          label="Large Touch Targets"
          description="Bigger buttons (48px minimum) for easier tapping"
          value={largeTouchTargets}
          onChange={toggleLargeTouchTargets}
          icon={<CursorArrowRaysIcon className="h-4 w-4" />}
        />
      </SettingsGroup>

      {/* About Section */}
      <div className="arcade-panel pixel-corners rounded-xl p-6 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-arcade-purple/20">
          <StarIcon className="h-6 w-6 text-arcade-purple" />
        </div>
        <p className="font-display text-sm font-bold text-white">Solante</p>
        <p className="mt-1 text-xs text-white/40">Roguelike pvp deck-builder wagering on Solana</p>
        <p className="mt-4 text-xs text-white/30">v0.1.0</p>
      </div>
    </motion.section>
  );
};
